import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ExternalLink, Plus, Archive } from "lucide-react";
import { api } from "../lib/api";
import { PageHeader, Panel } from "../ds";
import { Button } from "../components/ui/button";
import { NewProjectDialog } from "../components/NewProjectDialog";

export default function Projects() {
  const [showArchived, setShowArchived] = useState(false);
  const { data } = useQuery({
    queryKey: ["projects", showArchived],
    queryFn: async () =>
      (await api.get("/projects", { params: { include_archived: showArchived } })).data,
  });
  const projects = data?.projects || [];
  const activeCount = projects.filter((p) => p.status !== "archived").length;
  const [openNew, setOpenNew] = useState(false);

  // Active projects first, then archived at the bottom.
  const sorted = [...projects].sort((a, b) =>
    (a.status === "archived" ? 1 : 0) - (b.status === "archived" ? 1 : 0)
  );

  return (
    <div className="p-10 max-w-[1600px] mx-auto space-y-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <PageHeader
          title="Projects"
          subtitle={`${activeCount} active project${activeCount === 1 ? "" : "s"}`}
        />
        <div className="flex items-center gap-3">
          <label className="inline-flex items-center gap-2 text-xs font-mono text-[color:var(--muted)] cursor-pointer"
                 data-testid="show-archived-toggle">
            <input type="checkbox" checked={showArchived}
                   onChange={(e) => setShowArchived(e.target.checked)}
                   data-testid="show-archived-checkbox" />
            Show archived
          </label>
          <Button data-testid="new-project-btn" onClick={() => setOpenNew(true)}>
            <Plus className="h-4 w-4 mr-2" /> New project
          </Button>
        </div>
      </div>

      <Panel testId="projects-grid-panel">
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
          {sorted.map((p) => {
            const archived = p.status === "archived";
            return (
            <Link
              key={p.id}
              to={`/projects/${p.slug}`}
              className={`group block rounded-[7px] border border-[color:var(--line)] bg-[color:var(--panel-2)] p-4 hover:border-[color:var(--cobalt)] transition-colors relative ${archived ? "opacity-55" : ""}`}
              data-testid={`project-card-${p.slug}`}
            >
              {archived && (
                <span className="absolute top-2 right-2 inline-flex items-center gap-1 rounded-full border border-amber-500/30 bg-amber-500/10 px-1.5 py-0.5 text-[9px] font-mono uppercase tracking-wider text-amber-500"
                  data-testid={`archived-tag-${p.slug}`}>
                  <Archive className="h-2.5 w-2.5" /> archived
                </span>
              )}
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="font-mono text-[10.5px] uppercase tracking-[0.1em] text-[color:var(--muted)]">
                    {p.slug}
                  </p>
                  <h2 className="font-ui text-base font-semibold text-[color:var(--fg)] truncate">
                    {p.name}
                  </h2>
                  {p.parent_name && (
                    <p className="font-mono text-[10.5px] uppercase text-[color:var(--muted)] mt-1">
                      ↳ child of {p.parent_name}
                    </p>
                  )}
                </div>
                {!archived && <ExternalLink className="h-4 w-4 text-[color:var(--muted)] group-hover:text-[color:var(--cobalt)]" />}
              </div>
              <p className="text-xs text-[color:var(--muted)] mt-2 line-clamp-2">
                {p.description || "No description."}
              </p>
              {p.domain && (
                <p className="font-mono text-[10.5px] text-[color:var(--cobalt)] mt-3">{p.domain}</p>
              )}
            </Link>
          );})}
        </div>
      </Panel>

      <NewProjectDialog
        open={openNew}
        onOpenChange={setOpenNew}
        projects={projects}
      />
    </div>
  );
}
